'use client';

import { useState } from 'react';
import { ButtonBase } from '../../ButtonBase/ButtonBase';
import { css } from '@bts-web/utils-style-engine';
import { RegularChevronDownload, FilledSpinnerIos } from '../../../svgIcons';
import { sanitizeFilename } from '@bts-web/utils-functions';
import { useUserInfoContext } from '../../../userinfo/UserInfoContext/UserInfoProvider';

interface DownloadDocumentButtonProps {
  documentDownloadUrl: string | ((documentId: string) => Promise<string>);
  onDownloadCompleted: () => void;
  documentId: string;
  documentName: string;
  isOpenedAsLink?: boolean;
}

const DownloadDocumentButton = ({
  documentDownloadUrl,
  onDownloadCompleted,
  documentId,
  documentName,
  isOpenedAsLink,
}: DownloadDocumentButtonProps) => {
  const { refreshUserInfo } = useUserInfoContext();

  const [isLoading, setIsLoading] = useState(false);

  const onClickDownloadButton = async () => {
    let downloadUrl = documentDownloadUrl;

    if (typeof documentDownloadUrl !== 'string') {
      downloadUrl = await documentDownloadUrl(documentId);
    }

    if (isOpenedAsLink) {
      const linkElement = document.createElement('a');

      linkElement.href = downloadUrl as string;

      linkElement.rel = 'noopener noreferrer';

      // setTimeout is required for Safari mobile
      setTimeout(() => {
        linkElement.click();
      }, 0);

      return;
    }

    setIsLoading(true);

    fetch(downloadUrl as string)
      .then((response) => {
        if (!response.ok) {
          return;
        }

        response.blob().then((blob) => {
          const url = window.URL.createObjectURL(blob);

          const linkElement = document.createElement('a');

          linkElement['dataset'].testid = 'download-link';

          linkElement.href = url;

          const fileName = sanitizeFilename(documentName, 'document');

          linkElement.download = `${fileName}.pdf`;

          // setTimeout is required for Safari mobile
          setTimeout(() => {
            linkElement.click();
          }, 0);
        });
      })
      .finally(() => {
        setIsLoading(false);
      });

    refreshUserInfo();

    onDownloadCompleted();
  };

  return (
    <ButtonBase
      className={css({
        padding: '20px',
      })}
      onClick={onClickDownloadButton}
      disabled={isLoading}
      visual="ghost"
      size="small"
      noPadding
    >
      {isLoading ? (
        <FilledSpinnerIos size={16} />
      ) : (
        <RegularChevronDownload size={16} />
      )}
    </ButtonBase>
  );
};

export { DownloadDocumentButton };
